type NtcRow = {
  ntcId: number;
  nombre: string;
  total: number;
  antes: { C: number; NC: number; CP: number; NA: number; pct: number };
  despues: { C: number; NC: number; CP: number; NA: number; pct: number };
};

interface NtcResumenTableProps {
  rows?: NtcRow[];
  loading?: boolean;
}

function delta(main: number, secondary: number) {
  if (main === secondary) return <>{main}</>;
  return (
    <>
      {main} <span className="text-[11px] font-normal opacity-70">({secondary})</span>
    </>
  );
}

function pctDelta(main: number, secondary: number) {
  const fmt = (v: number) => v.toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';
  if (main === secondary) return <span className="font-bold">{fmt(main)}</span>;
  return (
    <span className="font-bold">
      {fmt(main)} <span className="text-[11px] font-normal opacity-70">({fmt(secondary)})</span>
    </span>
  );
}

function SkeletonRow() {
  return (
    <tr className="h-9" style={{ borderBottom: '1px solid var(--admin-border)' }}>
      {[false, true, true, true, true, true, true].map((centered, i) => (
        <td key={i} className="px-2 align-middle">
          <div
            className="rounded animate-pulse"
            style={{
              width: centered ? '60%' : '80%',
              height: 12,
              background: 'var(--admin-border)',
              margin: centered ? '0 auto' : undefined,
            }}
          />
        </td>
      ))}
    </tr>
  );
}

export function NtcResumenTable({ rows, loading }: NtcResumenTableProps) {
  const hasDelta =
    !loading && rows
      ? rows.some(
          (r) => r.antes.C !== r.despues.C || r.antes.NC !== r.despues.NC || r.antes.CP !== r.despues.CP || r.antes.NA !== r.despues.NA
        )
      : false;

  const totalDespues = rows
    ? rows.reduce(
        (acc, r) => ({
          C: acc.C + r.despues.C,
          NC: acc.NC + r.despues.NC,
          CP: acc.CP + r.despues.CP,
          NA: acc.NA + r.despues.NA,
          total: acc.total + r.total,
        }),
        { C: 0, NC: 0, CP: 0, NA: 0, total: 0 }
      )
    : { C: 0, NC: 0, CP: 0, NA: 0, total: 0 };

  const totalAntes = rows
    ? rows.reduce((acc, r) => ({ C: acc.C + r.antes.C, NC: acc.NC + r.antes.NC, CP: acc.CP + r.antes.CP, NA: acc.NA + r.antes.NA }), {
        C: 0,
        NC: 0,
        CP: 0,
        NA: 0,
      })
    : { C: 0, NC: 0, CP: 0, NA: 0 };

  const avgPctDespues = rows && rows.length > 0 ? rows.reduce((s, r) => s + r.despues.pct, 0) / rows.length : 0;
  const avgPctAntes = rows && rows.length > 0 ? rows.reduce((s, r) => s + r.antes.pct, 0) / rows.length : 0;

  if (!loading && (!rows || rows.length === 0)) return null;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[12px] border-collapse table-fixed">
        <colgroup>
          <col className="w-[40%]" />
          <col className="w-[8%]" />
          <col className="w-[8%]" />
          <col className="w-[8%]" />
          <col className="w-[8%]" />
          <col className="w-[8%]" />
          <col className="w-[20%]" />
        </colgroup>
        <thead>
          <tr className="h-9" style={{ background: 'var(--admin-surface2)', color: 'var(--admin-text2)' }}>
            <th className="text-left px-2 align-middle font-semibold text-[10px] uppercase tracking-wider">Norma Técnica</th>
            <th className="text-center px-2 align-middle font-semibold text-[10px] uppercase tracking-wider">Total general</th>
            <th className="text-center px-2 align-middle font-semibold text-[10px] uppercase tracking-wider">C</th>
            <th className="text-center px-2 align-middle font-semibold text-[10px] uppercase tracking-wider">NC</th>
            <th className="text-center px-2 align-middle font-semibold text-[10px] uppercase tracking-wider">CP</th>
            <th className="text-center px-2 align-middle font-semibold text-[10px] uppercase tracking-wider">NA</th>
            <th className="text-center px-2 align-middle font-semibold text-[10px] uppercase tracking-wider">% Cumplimiento</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <>
              {Array.from({ length: 3 }, (_, i) => (
                <SkeletonRow key={i} />
              ))}
              <tr
                className="h-9"
                style={{ background: 'var(--admin-surface2)', fontWeight: 600, borderTop: '2px solid var(--admin-border)' }}
              >
                <td className="px-2 align-middle" style={{ color: 'var(--admin-text)' }}>
                  Total general
                </td>
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <td key={i} className="px-2 align-middle">
                    <div className="rounded animate-pulse h-3 w-3/4 mx-auto" style={{ background: 'var(--admin-border)' }} />
                  </td>
                ))}
              </tr>
            </>
          ) : (
            rows!.map((r) => (
              <tr key={r.ntcId} className="h-9" style={{ borderBottom: '1px solid var(--admin-border)' }}>
                <td className="px-2 align-middle font-medium" style={{ color: 'var(--admin-text)' }}>
                  {r.nombre}
                </td>
                <td className="text-center px-2 align-middle" style={{ color: 'var(--admin-text2)' }}>
                  {r.total}
                </td>
                <td className="text-center px-2 align-middle" style={{ color: 'var(--admin-text)' }}>
                  {delta(r.despues.C, r.antes.C)}
                </td>
                <td className="text-center px-2 align-middle" style={{ color: 'var(--admin-text)' }}>
                  {delta(r.despues.NC, r.antes.NC)}
                </td>
                <td className="text-center px-2 align-middle" style={{ color: 'var(--admin-text)' }}>
                  {delta(r.despues.CP, r.antes.CP)}
                </td>
                <td className="text-center px-2 align-middle" style={{ color: 'var(--admin-text2)' }}>
                  {delta(r.despues.NA, r.antes.NA)}
                </td>
                <td className="text-center px-2 align-middle">{pctDelta(r.despues.pct, r.antes.pct)}</td>
              </tr>
            ))
          )}
          {!loading && rows && rows.length > 0 && (
            <tr
              className="h-9"
              style={{ background: 'var(--admin-surface2)', fontWeight: 600, borderTop: '2px solid var(--admin-border)' }}
            >
              <td className="px-2 align-middle" style={{ color: 'var(--admin-text)' }}>
                Total general
              </td>
              <td className="text-center px-2 align-middle" style={{ color: 'var(--admin-text)' }}>
                {totalDespues.total}
              </td>
              <td className="text-center px-2 align-middle" style={{ color: 'var(--admin-text)' }}>
                {delta(totalDespues.C, totalAntes.C)}
              </td>
              <td className="text-center px-2 align-middle" style={{ color: 'var(--admin-text)' }}>
                {delta(totalDespues.NC, totalAntes.NC)}
              </td>
              <td className="text-center px-2 align-middle" style={{ color: 'var(--admin-text)' }}>
                {delta(totalDespues.CP, totalAntes.CP)}
              </td>
              <td className="text-center px-2 align-middle" style={{ color: 'var(--admin-text2)' }}>
                {delta(totalDespues.NA, totalAntes.NA)}
              </td>
              <td className="text-center px-2 align-middle">
                {pctDelta(Number(avgPctDespues.toFixed(2)), Number(avgPctAntes.toFixed(2)))}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <div
        className={`flex items-center gap-3 px-2 text-[10px] h-9 ${hasDelta ? '' : 'invisible'}`}
        style={{ color: 'var(--admin-text3)' }}
      >
        <span className="flex items-center gap-1">
          <span className="font-semibold" style={{ color: 'var(--admin-text2)' }}>
            Después
          </span>
          <span>→</span>
          <span className="opacity-70">(Antes)</span>
        </span>
        <span>·</span>
        <span>Los valores entre paréntesis son el estado previo a esta asesoría</span>
      </div>
    </div>
  );
}
